// image-transform service base (override per-env via REACT_APP_IMAGE_TRANSFORM_BASE).
const IMAGE_TRANSFORM_BASE = (
  process.env.REACT_APP_IMAGE_TRANSFORM_BASE ||
  "https://image-transform.materialdepot.com"
).replace(/\/+$/, "");

// Canonical hosts referenced throughout.
const IMAGE_HOST = "materialdepot-images-hbh2cjbvbtfmanhx.z02.azurefd.net";
const CONTENT_HOST =
  "materialdepot-content-files-endpoint-e8cnf0c2gxfhe5fb.z02.azurefd.net";
const CONTENT_NORESIZE_HOST =
  "material-depot-content-files-noresize-endpoint-bsbkh4asdwecc9dp.z02.azurefd.net";
const R2_IMAGES = "pub-132f3882c2074e84999a9ab982950552.r2.dev";
const R2_CONTENT = "pub-572d4732b7644138867808415beb5a4f.r2.dev";

type Swap = [string, string];

// Apply ordered [from, to] host swaps. Null-safe so a nullish input passes through.
const swap = (url: string, pairs: Swap[]): string =>
  pairs.reduce((u, [from, to]) => u?.replaceAll(from, to), url);

const isMedia = (u?: string): boolean =>
  !!u &&
  (u.includes(".mp4") ||
    u.includes(".mov") ||
    u.includes(".gif") ||
    u.includes(".svg"));

// Media (video/gif/svg) bypasses image-transform, so these swaps are the final output.
const MEDIA_SWAPS: Swap[] = [
  [IMAGE_HOST, R2_IMAGES],
  [CONTENT_HOST, CONTENT_NORESIZE_HOST],
  ["dpy2z8n9cxui1.cloudfront.net", "d2cwt1uuomj2h5.cloudfront.net"],
];

// Source host -> image-transform bucket prefix (first match wins). Covers both
// the raw source domains and the endpoints getImageUrl normalises into.
//   main / azure -> R2 `materialdepotimages`         (WebP)
//   content      -> R2 `material-depot-content-files` (no WebP)
const BUCKET_BY_DOMAIN: Swap[] = [
  [IMAGE_HOST, "azure"],
  ["materialdepotimages.materialdepot.in", "main"],
  ["materialdepotimages.materialdepot.com", "main"],
  ["materialdepotimages.s3.ap-south-1.amazonaws.com", "main"],
  ["materialdepotimages.s3.amazonaws.com", "main"],
  [R2_IMAGES, "main"],
  [CONTENT_HOST, "content"],
  [CONTENT_NORESIZE_HOST, "content"],
  [R2_CONTENT, "content"],
  ["material-depot-content-files.s3.ap-south-1.amazonaws.com", "content"],
  ["materialdepot-content-files.materialdepot.in", "content"],
  ["dqzffhb3lxxp.cloudfront.net", "content"],
  ["d3faqy0icgqzj8.cloudfront.net", "content"],
];

/**
 * Convert a source (or already-normalised) image URL into an image-transform
 * service URL: `${BASE}/<bucket>/<path>?width=<N>&format=webp`.
 *  - WebP is added for the image buckets (main/azure); `content` is resize-only
 *    (the service never WebP-converts content).
 *  - Videos / GIF / SVG and unknown hosts are returned unchanged.
 *  - Idempotent: an existing image-transform URL is returned as-is.
 */
function toImageTransform(url: string, width: string): string {
  if (!url) return "";

  // Idempotent: already an image-transform URL.
  if (url.startsWith(IMAGE_TRANSFORM_BASE)) return url;

  const lower = url.toLowerCase();
  if (/\.(mp4|mov|gif|svg)(\?|$)/.test(lower)) return url;

  const [base, qs] = url.split("?");
  let height = width;
  if (qs) {
    const p = new URLSearchParams(qs);
    height = p.get("height") || p.get("width") || width;
  }

  for (const [domain, bucket] of BUCKET_BY_DOMAIN) {
    const idx = base.indexOf(domain);
    if (idx === -1) continue;
    const objectPath = base
      .slice(idx + domain.length)
      .replace(/^\/+/, "")
      .replaceAll("+", "%20");
    const params: string[] = [];
    if (height) params.push("width=" + height);
    if (bucket !== "content") params.push("format=webp");
    return `${IMAGE_TRANSFORM_BASE}/${bucket}/${objectPath}${
      params.length ? "?" + params.join("&") : ""
    }`;
  }

  return url;
}

// Shared tail: absolutise the URL, encode spaces, optionally append width, transform.
function finalize(img_url: string, width: string): string {
  if (img_url && img_url[0] === "/") img_url = "https://" + IMAGE_HOST + img_url;
  img_url = img_url?.replaceAll("+", "%20");
  if (!img_url?.startsWith("https")) img_url = "https://" + IMAGE_HOST + img_url;
  if (width !== "") img_url += "?width=" + width;
  return toImageTransform(img_url, width);
}

export function getImageUrl(path: string, width: string) {
  if (path?.includes("webp")) {
    const webp = swap(path, [
      ["materialdepotimages.materialdepot.in", R2_IMAGES],
      ["materialdepotimages.s3.amazonaws.com", R2_IMAGES],
      ["material-depot-content-files.s3.ap-south-1.amazonaws.com", R2_CONTENT],
      [
        "materialdepotimages.s3.ap-south-1.amazonaws.com",
        "materialdepotimages.materialdepot.in",
      ],
    ]);
    return toImageTransform(webp, width);
  }

  let img = swap(path, [
    ["materialdepotimages.materialdepot.in", IMAGE_HOST],
    ["materialdepotimages.s3.ap-south-1.amazonaws.com", IMAGE_HOST],
    [R2_IMAGES, IMAGE_HOST],
    ["materialdepotimages.s3.amazonaws.com", IMAGE_HOST],
    ["material-depot-content-files.s3.ap-south-1.amazonaws.com", CONTENT_HOST],
    ["dqzffhb3lxxp.cloudfront.net", CONTENT_HOST],
    ["d3faqy0icgqzj8.cloudfront.net", CONTENT_HOST],
  ]);

  if (isMedia(img)) img = swap(img, MEDIA_SWAPS);

  return finalize(img, width);
}

export const reducedImageUrl = (
  percent: number,
  imgUrl: string,
  isMobile: boolean | null | undefined
) => {
  if (percent === 100 || !imgUrl) {
    return imgUrl;
  }

  let newUrl = swap(imgUrl, [
    [
      "materialdepotimages.s3.ap-south-1.amazonaws.com",
      "dpy2z8n9cxui1.cloudfront.net",
    ],
    [
      "material-depot-content-files.s3.ap-south-1.amazonaws.com",
      "d3faqy0icgqzj8.cloudfront.net",
    ],
  ]);

  if (newUrl[0] === "/") newUrl = "https://dpy2z8n9cxui1.cloudfront.net" + newUrl;
  newUrl = newUrl.replaceAll("+", "%20");

  const SMALL: Record<number, number> = { 20: 100, 40: 200, 60: 360, 80: 460 };
  const LARGE: Record<number, number> = { 20: 100, 40: 300, 60: 800, 80: 1200 };
  const MOBILE: Record<number, number> = { 20: 40, 40: 80, 60: 160, 80: 360 };

  const table = !isMobile
    ? window.innerWidth < 768
      ? SMALL
      : LARGE
    : isMobile === true
    ? MOBILE
    : LARGE;

  const w = table[percent];
  if (w) newUrl += "?width=" + w;

  return newUrl;
};

export const getImage = (
  page: string,
  section: string,
  imgUrl: string,
  isMobile: any
) => {
  const path = imgUrl;
  const width = "";

  if (path?.includes("webp")) {
    const webp = swap(path, [
      ["materialdepotimages.s3.ap-south-1.amazonaws.com", R2_IMAGES],
      ["materialdepotimages.s3.amazonaws.com", R2_IMAGES],
      ["material-depot-content-files.s3.ap-south-1.amazonaws.com", R2_CONTENT],
    ]);
    return toImageTransform(webp, width);
  }

  let img = swap(path, [
    ["materialdepotimages.s3.ap-south-1.amazonaws.com", IMAGE_HOST],
    [R2_IMAGES, IMAGE_HOST],
    ["materialdepotimages.s3.amazonaws.com", IMAGE_HOST],
    ["material-depot-content-files.s3.ap-south-1.amazonaws.com", CONTENT_HOST],
    ["d3faqy0icgqzj8.cloudfront.net", CONTENT_HOST],
  ]);

  if (img?.includes(".png") && img.includes(IMAGE_HOST)) {
    img = img.replaceAll(IMAGE_HOST, "materialdepotimages.materialdepot.in");
  }

  if (isMedia(img)) img = swap(img, MEDIA_SWAPS);

  if (img?.includes(".png") && img.includes(CONTENT_HOST)) {
    img = img.replaceAll(CONTENT_HOST, CONTENT_NORESIZE_HOST);
  }

  return finalize(img, width);
};
